import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Loader2, CheckCircle2, XCircle, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import api from "@/lib/api";

export default function SubscriptionCallback() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [status, setStatus] = useState('verifying');
    const [message, setMessage] = useState('');
    const [subscriptionData, setSubscriptionData] = useState(null);

    useEffect(() => {
        verifyPayment();
    }, []);

    const verifyPayment = async () => {
        try {
            const reference = searchParams.get('reference');
            const trxref = searchParams.get('trxref');
            const paystackStatus = searchParams.get('status');

            const paymentReference = reference || trxref;

            if (!paymentReference) {
                setStatus('error');
                setMessage('No payment reference found in URL');
                return;
            }

            if (paystackStatus === 'cancelled') {
                setStatus('failed');
                setMessage('Payment was cancelled. No charges were made.');
                setTimeout(() => navigate('/subscription'), 3000);
                return;
            }

            const token = localStorage.getItem('access_token');
            if (!token) {
                setStatus('error');
                setMessage('Please login to verify payment');
                setTimeout(() => navigate('/login'), 2000);
                return;
            }

            const response = await api.get(`/subscriptions/verify/${paymentReference}`);

            if (response.data.status === 'success') {
                setStatus('success');
                setMessage(response.data.message || 'Payment verified successfully!');
                
                const isCredits = paymentReference.startsWith('CRD-');
                
                setSubscriptionData({
                    plan: response.data.plan || (isCredits ? 'Credits Purchase' : 'Subscription'),
                    status: 'active',
                    expires: response.data.end_date,
                    credits_added: response.data.credits_added,
                    total_credits: response.data.total_credits
                });

                // Fetch and store updated user data
                try {
                    const userRes = await api.get('/auth/me');
                    localStorage.setItem('user', JSON.stringify(userRes.data));
                    console.log('Updated user data:', userRes.data);
                } catch (err) {
                    console.error('Failed to refresh user data:', err);
                }

                // Redirect after delay - use window.location for hard reload
                setTimeout(() => {
                    window.location.href = '/subscription?success=true';
                }, 2000);
            } else {
                setStatus('failed');
                const errorMsg = response.data.message || response.data.error;
                setMessage(typeof errorMsg === 'string' ? errorMsg : 'Payment verification failed');
                setTimeout(() => navigate('/subscription'), 3000);
            }

        } catch (error) {
            console.error('Payment verification error:', error);
            setStatus('error');
            
            if (error.response?.status === 401) {
                setMessage('Session expired. Please login again.');
                setTimeout(() => navigate('/login'), 2000);
            } else if (error.response?.status === 404) {
                setMessage('Transaction not found. Please contact support if you were charged.');
            } else {
                const errorData = error.response?.data;
                let errorMsg = 'Failed to verify payment. Please contact support.';
                if (errorData) {
                    if (typeof errorData.error === 'string') errorMsg = errorData.error;
                    else if (typeof errorData.message === 'string') errorMsg = errorData.message;
                }
                setMessage(errorMsg);
            }
        }
    };

    const handleReturnToSubscription = () => {
        window.location.href = '/subscription';
    };

    const handleContactSupport = () => {
        window.location.href = 'mailto:support@trimy.app?subject=Payment Verification Issue';
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-background p-4">
            <Card className="max-w-md w-full">
                <CardContent className="pt-12 pb-8 px-8">
                    {status === 'verifying' && (
                        <div className="text-center space-y-4">
                            <div className="w-20 h-20 mx-auto rounded-full bg-blue-100 flex items-center justify-center">
                                <Loader2 className="w-10 h-10 text-blue-600 animate-spin" />
                            </div>
                            <div>
                                <h2 className="text-2xl font-bold mb-2">Verifying Payment</h2>
                                <p className="text-muted-foreground">Please wait...</p>
                            </div>
                        </div>
                    )}

                    {status === 'success' && (
                        <div className="text-center space-y-4">
                            <div className="w-20 h-20 mx-auto rounded-full bg-green-100 flex items-center justify-center">
                                <CheckCircle2 className="w-10 h-10 text-green-600" />
                            </div>
                            <div>
                                <h2 className="text-2xl font-bold mb-2">Payment Successful!</h2>
                                <p className="text-muted-foreground mb-4">{message}</p>

                                {subscriptionData && (
                                    <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-left space-y-2 mb-4">
                                        {subscriptionData.credits_added && (
                                            <div className="flex justify-between text-sm">
                                                <span className="text-muted-foreground">Credits Added:</span>
                                                <span className="font-semibold text-green-600">
                                                    +{subscriptionData.credits_added.toLocaleString()}
                                                </span>
                                            </div>
                                        )}
                                        {subscriptionData.total_credits && (
                                            <div className="flex justify-between text-sm">
                                                <span className="text-muted-foreground">Total Credits:</span>
                                                <span className="font-semibold">
                                                    {subscriptionData.total_credits.toLocaleString()}
                                                </span>
                                            </div>
                                        )}
                                        {subscriptionData.plan && subscriptionData.plan !== 'Credits Purchase' && (
                                            <div className="flex justify-between text-sm">
                                                <span className="text-muted-foreground">Plan:</span>
                                                <span className="font-semibold">{subscriptionData.plan}</span>
                                            </div>
                                        )}
                                    </div>
                                )}

                                <p className="text-sm text-muted-foreground">Redirecting...</p>
                            </div>

                            <Button onClick={handleReturnToSubscription} className="w-full mt-4">
                                Go to Dashboard
                            </Button>
                        </div>
                    )}

                    {status === 'failed' && (
                        <div className="text-center space-y-4">
                            <div className="w-20 h-20 mx-auto rounded-full bg-red-100 flex items-center justify-center">
                                <XCircle className="w-10 h-10 text-red-600" />
                            </div>
                            <div>
                                <h2 className="text-2xl font-bold mb-2">Payment Failed</h2>
                                <p className="text-muted-foreground mb-4">{message}</p>
                            </div>
                            <div className="flex flex-col gap-2">
                                <Button onClick={handleReturnToSubscription} className="w-full">Try Again</Button>
                                <Button onClick={handleContactSupport} variant="outline" className="w-full">Contact Support</Button>
                            </div>
                        </div>
                    )}

                    {status === 'error' && (
                        <div className="text-center space-y-4">
                            <div className="w-20 h-20 mx-auto rounded-full bg-amber-100 flex items-center justify-center">
                                <AlertCircle className="w-10 h-10 text-amber-600" />
                            </div>
                            <div>
                                <h2 className="text-2xl font-bold mb-2">Verification Error</h2>
                                <p className="text-muted-foreground mb-4">{message}</p>
                                <div className="bg-muted border rounded-lg p-3 mb-4">
                                    <p className="text-xs text-muted-foreground text-left">
                                        <strong>Reference:</strong><br/>
                                        <code>{searchParams.get('reference') || searchParams.get('trxref') || 'N/A'}</code>
                                    </p>
                                </div>
                            </div>
                            <div className="flex flex-col gap-2">
                                <Button onClick={handleContactSupport} className="w-full">Contact Support</Button>
                                <Button onClick={handleReturnToSubscription} variant="outline" className="w-full">Return</Button>
                            </div>
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}